// Pulse Nova force effects (owner 2026-11-02: 'add more force effects to Pulse Nova, should see wind, energy waves pushing
// outward'). Render-only, reacts to the sim's 'nova' / 'novaBurst' events:
//  - wind streaks: thin bright lines racing outward from the hero, low along the ground and a few through the air, stretched
//    along their direction of travel (speed lines), fading as they go
//  - energy walls: translucent expanding cylinder bands (bright lower edge, fading top) — one with the pulse, a taller,
//    stronger one when the sphere bursts — so the push reads as a wave front sweeping outward
// Pooled instanced meshes, no per-frame allocation.
import * as THREE from 'three/webgpu';
import { abs, attribute, color, float, mix, smoothstep, uv } from 'three/tsl';

const MAX_STREAKS = 160, MAX_WALLS = 6;
const _m = new THREE.Matrix4(), _p = new THREE.Vector3(), _q = new THREE.Quaternion(), _s = new THREE.Vector3(), _e = new THREE.Euler();

interface Streak { on: boolean; x: number; y: number; z: number; dx: number; dz: number; d: number; sp: number; len: number; w: number; t: number; life: number }
interface Wall { on: boolean; x: number; y: number; z: number; r0: number; r1: number; h: number; t: number; life: number; gold: number; k: number }

export class NovaForce {
  private streakMesh: THREE.InstancedMesh; private wallMesh: THREE.InstancedMesh;
  private streaks: Streak[] = []; private walls: Wall[] = [];
  private sA: THREE.InstancedBufferAttribute; private wA: THREE.InstancedBufferAttribute;

  constructor(scene: THREE.Scene, private groundAt: (x: number, z: number) => number) {
    // streak: a flat strip along +X (local), uv.x = 0 tail .. 1 head; a = fade, b = gold
    const sg = new THREE.PlaneGeometry(1, 1).rotateX(-Math.PI / 2).translate(0.5, 0, 0);
    this.sA = new THREE.InstancedBufferAttribute(new Float32Array(MAX_STREAKS * 2), 2);
    sg.setAttribute('sv', this.sA);
    const sm = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide, fog: false });
    const sv = attribute('sv', 'vec2');
    const across = abs(uv().y.sub(0.5)).mul(2), along = uv().x;
    const core = smoothstep(float(1), float(0.15), across).mul(along.pow(1.6)).mul(smoothstep(float(1), float(0.85), along));
    sm.colorNode = mix(color(0xbfeeff), color(0xffe2a0), sv.y).mul(core).mul(sv.x).mul(1.6);
    this.streakMesh = new THREE.InstancedMesh(sg, sm, MAX_STREAKS);
    // wall: open cylinder band, radius 1, height 1 from y 0; uv.y = 0 bottom .. 1 top; a = fade, b = gold
    const wg = new THREE.CylinderGeometry(1, 1, 1, 64, 1, true).translate(0, 0.5, 0);
    this.wA = new THREE.InstancedBufferAttribute(new Float32Array(MAX_WALLS * 2), 2);
    wg.setAttribute('wv', this.wA);
    const wm = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide, fog: false });
    const wv = attribute('wv', 'vec2'), h = uv().y;
    const band = smoothstep(float(1), float(0.0), h).pow(1.8).add(smoothstep(float(0.12), float(0.0), h).mul(1.2)); // bright foot, fading top
    const ripples = h.mul(18).add(uv().x.mul(62.8)).sin().mul(0.12).add(0.88);
    wm.colorNode = mix(color(0x7fe0ff), color(0xffc860), wv.y).mul(band).mul(ripples).mul(wv.x).mul(0.9);
    this.wallMesh = new THREE.InstancedMesh(wg, wm, MAX_WALLS);
    for (const m of [this.streakMesh, this.wallMesh]) { m.frustumCulled = false; m.count = 0; m.renderOrder = 4; scene.add(m); }
    for (let i = 0; i < MAX_STREAKS; i++) this.streaks.push({ on: false, x: 0, y: 0, z: 0, dx: 1, dz: 0, d: 0, sp: 0, len: 1, w: 0.1, t: 0, life: 1 });
    for (let i = 0; i < MAX_WALLS; i++) this.walls.push({ on: false, x: 0, y: 0, z: 0, r0: 0, r1: 1, h: 1, t: 0, life: 1, gold: 0, k: 1 });
  }

  /** a pulse (r = sphere radius) or the sphere's burst (strong) at (x, z) */
  push(x: number, z: number, r: number, gold: boolean, strong: boolean): void {
    const gy = this.groundAt(x, z), n = strong ? 44 : 30;
    for (let k = 0; k < n; k++) {
      const s = this.streaks.find((q) => !q.on); if (!s) break;
      const a = Math.random() * Math.PI * 2, high = Math.random() < 0.3;
      s.on = true; s.x = x; s.z = z; s.dx = Math.cos(a); s.dz = Math.sin(a);
      s.y = gy + (high ? 0.6 + Math.random() * 1.4 : 0.08 + Math.random() * 0.25);
      s.d = r * (strong ? 0.6 : 0.35) * (0.6 + Math.random() * 0.5);
      s.sp = r * (strong ? 3.6 : 2.8) * (0.8 + Math.random() * 0.5);
      s.len = (strong ? 1.8 : 1.3) * (0.6 + Math.random() * 0.8) * Math.max(1, r / 4);
      s.w = (high ? 0.06 : 0.09) * (0.7 + Math.random() * 0.6);
      s.t = 0; s.life = (strong ? 0.5 : 0.42) * (0.75 + Math.random() * 0.5);
      (s as Streak & { gold?: number }).gold = gold ? 1 : 0;
    }
    const w = this.walls.find((q) => !q.on); if (!w) return;
    Object.assign(w, { on: true, x, z, y: gy, r0: r * (strong ? 0.8 : 0.4), r1: r * (strong ? 1.45 : 1.15), h: strong ? 2.2 : 1.3, t: 0, life: strong ? 0.42 : 0.34, gold: gold ? 1 : 0, k: strong ? 1.2 : 0.8 });
  }

  update(dt: number): void {
    let n = 0;
    const sa = this.sA.array as Float32Array;
    for (const s of this.streaks) {
      if (!s.on) continue;
      s.t += dt; if (s.t >= s.life) { s.on = false; continue; }
      const u = s.t / s.life, ease = 1 - (1 - u) * (1 - u);
      const d = s.d + s.sp * s.life * ease;
      _p.set(s.x + s.dx * d, s.y, s.z + s.dz * d);
      _e.set(0, -Math.atan2(s.dz, s.dx), 0); _q.setFromEuler(_e);
      _s.set(s.len * (0.6 + 0.6 * u), 1, s.w);
      _p.x -= s.dx * _s.x; _p.z -= s.dz * _s.x; // the head leads; the strip trails behind it
      this.streakMesh.setMatrixAt(n, _m.compose(_p, _q, _s));
      sa[n * 2] = Math.sin(Math.PI * Math.min(1, u * 1.4)) * (1 - u * 0.5); sa[n * 2 + 1] = (s as Streak & { gold?: number }).gold ?? 0;
      n++;
    }
    this.streakMesh.count = n; if (n) { this.streakMesh.instanceMatrix.needsUpdate = true; this.sA.needsUpdate = true; }
    let m = 0;
    const wa = this.wA.array as Float32Array;
    for (const w of this.walls) {
      if (!w.on) continue;
      w.t += dt; if (w.t >= w.life) { w.on = false; continue; }
      const u = w.t / w.life, ease = 1 - Math.pow(1 - u, 2.2), R = w.r0 + (w.r1 - w.r0) * ease;
      _p.set(w.x, w.y, w.z); _q.identity(); _s.set(R, w.h * (1 - 0.35 * u), R);
      this.wallMesh.setMatrixAt(m, _m.compose(_p, _q, _s));
      wa[m * 2] = (1 - u) * (1 - u) * w.k; wa[m * 2 + 1] = w.gold;
      m++;
    }
    this.wallMesh.count = m; if (m) { this.wallMesh.instanceMatrix.needsUpdate = true; this.wA.needsUpdate = true; }
  }

  clear(): void { for (const s of this.streaks) s.on = false; for (const w of this.walls) w.on = false; this.streakMesh.count = 0; this.wallMesh.count = 0; }
}
